import { useQueries, useQuery } from '@tanstack/react-query';
import { apiFetch } from './client.js';
import type { DashboardMention, DashboardRecentInvoice, Invoice, Validation } from './types.js';

export interface DashboardPayload {
  assignedToMe: Invoice[];
  awaitingMyValidation: Validation[];
}

export interface AggregatedDashboard {
  data: DashboardPayload | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Aggregates `GET /api/dashboard/me?tenantId=…` across all the caller's
 * tenants. The contract endpoint is per-tenant; we fan out one request
 * per membership and merge. Returns `null` until the first batch
 * resolves (loading) or every batch errors (isError).
 */
export function useDashboard(tenantIds: string[]): AggregatedDashboard {
  const results = useQueries({
    queries: tenantIds.map((tid) => ({
      queryKey: ['dashboard', tid] as const,
      queryFn: () =>
        apiFetch<DashboardPayload>(`/dashboard/me?tenantId=${encodeURIComponent(tid)}`),
      staleTime: 30_000,
    })),
  });

  const isLoading = tenantIds.length > 0 && results.some((r) => r.isLoading);
  const isError = tenantIds.length > 0 && results.every((r) => r.isError);
  const allDone = results.every((r) => !r.isLoading);
  if (!allDone || tenantIds.length === 0) {
    return {
      data: tenantIds.length === 0 ? { assignedToMe: [], awaitingMyValidation: [] } : null,
      isLoading,
      isError,
      refetch: () => results.forEach((r) => void r.refetch()),
    };
  }

  const assignedToMe: Invoice[] = [];
  const awaitingMyValidation: Validation[] = [];
  for (const r of results) {
    if (r.data) {
      assignedToMe.push(...r.data.assignedToMe);
      awaitingMyValidation.push(...r.data.awaitingMyValidation);
    }
  }
  // Sort by dueDate ascending, nulls last.
  assignedToMe.sort((a, b) => compareDue(a.dueDate, b.dueDate));
  return {
    data: { assignedToMe, awaitingMyValidation },
    isLoading: false,
    isError,
    refetch: () => results.forEach((r) => void r.refetch()),
  };
}

function compareDue(a: string | null | undefined, b: string | null | undefined): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b);
}

// REQ-FE-029c-2 — cross-tenant aggregator hooks consuming REQ-BE-029-be
// B + C. Both endpoints return arrays sorted server-side; we just
// surface them with a sensible default limit.

export function useDashboardRecentInvoices(limit = 5) {
  return useQuery<DashboardRecentInvoice[]>({
    queryKey: ['dashboard-recent-invoices', limit] as const,
    queryFn: () => apiFetch<DashboardRecentInvoice[]>(`/dashboard/recent-invoices?limit=${limit}`),
    staleTime: 60_000,
  });
}

export function useDashboardMentions(limit = 20) {
  return useQuery<DashboardMention[]>({
    queryKey: ['dashboard-mentions', limit] as const,
    queryFn: () => apiFetch<DashboardMention[]>(`/dashboard/mentions?limit=${limit}`),
    staleTime: 60_000,
  });
}
